import { toast } from "$global-providers/toast-provider";
import { Box, Button, styled, TextField, Typography } from "@material-ui/core";
import type { EpocusRole } from "elephant-web/gql";
import { phone } from "phone";
import { type FC, useState } from "react";
import {
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { roleLabel } from "../../_lib/rbac/role-label";
import { useUpdateMyEpocusUserMutation } from "./__generated__/epocus-settings-operations.gql";
import { isValidNpi, NPI_ERROR_MESSAGE, normaliseNpi } from "./npi";
import { SettingsCard, SettingsCardLabel } from "./settings-card";

const FieldsColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: rem(4),
});

const RoleRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 8,
});

const RoleChip = styled("span")({
  backgroundColor: EPOCUS_SELECTED_BG,
  color: EPOCUS_PRIMARY,
  fontSize: rem(10),
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "3px 8px",
  borderRadius: 999,
});

const RoleProgram = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const PHONE_ERROR_MESSAGE =
  "Enter a valid mobile number in international format, e.g. +234…";

export type AccountMembership = {
  programId: string;
  programName: string;
  role: EpocusRole;
};

export const AccountCard: FC<{
  firstName: string;
  lastName: string;
  email: string;
  telephone?: string | null;
  npi?: string | null;
  memberships: readonly AccountMembership[];
}> = ({
  firstName: initialFirstName,
  lastName: initialLastName,
  email,
  telephone,
  npi,
  memberships,
}) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [telephoneValue, setTelephoneValue] = useState(telephone ?? "");
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [telephoneError, setTelephoneError] = useState(false);
  const [npiValue, setNpiValue] = useState(npi ?? "");
  const [npiError, setNpiError] = useState(false);

  const [updateMyEpocusUser, { loading }] = useUpdateMyEpocusUserMutation({
    onCompleted: () => toast.success("Account details saved"),
    onError: (e) => toast.error(`Couldn't save: ${e.message}`),
  });

  const handleSave = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedTelephone = telephoneValue.trim();
    const isFirstNameInvalid = trimmedFirstName.length === 0;
    const isLastNameInvalid = trimmedLastName.length === 0;
    const isTelephoneInvalid =
      trimmedTelephone !== "" && !phone(trimmedTelephone).isValid;

    setFirstNameError(isFirstNameInvalid);
    setLastNameError(isLastNameInvalid);
    setTelephoneError(isTelephoneInvalid);
    const trimmedNpi = normaliseNpi(npiValue);
    const isNpiInvalid = trimmedNpi !== "" && !isValidNpi(trimmedNpi);
    setNpiError(isNpiInvalid);

    if (
      isFirstNameInvalid ||
      isLastNameInvalid ||
      isTelephoneInvalid ||
      isNpiInvalid
    ) {
      return;
    }

    await updateMyEpocusUser({
      variables: {
        input: {
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          telephone: trimmedTelephone
            ? phone(trimmedTelephone).phoneNumber || trimmedTelephone
            : null,
          npi: trimmedNpi || null,
        },
      },
    });
  };

  return (
    <SettingsCard>
      <SettingsCardLabel>Account</SettingsCardLabel>

      {memberships.length > 0 && (
        <RoleRow>
          {memberships.map(({ programId, programName, role }) => (
            <RoleRow key={programId}>
              <RoleChip>{roleLabel(role)}</RoleChip>
              <RoleProgram>{programName}</RoleProgram>
            </RoleRow>
          ))}
        </RoleRow>
      )}

      <FieldsColumn>
        <TextField
          id="epocus-settings-first-name"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          label="First name"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            setFirstNameError(false);
          }}
          error={firstNameError}
          helperText={firstNameError ? "First name is required" : undefined}
        />
        <TextField
          id="epocus-settings-last-name"
          variant="outlined"
          margin="dense"
          fullWidth
          required
          label="Last name"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            setLastNameError(false);
          }}
          error={lastNameError}
          helperText={lastNameError ? "Last name is required" : undefined}
        />
        <TextField
          id="epocus-settings-email"
          variant="outlined"
          margin="dense"
          fullWidth
          disabled
          label="Email"
          value={email}
          helperText="Your email is how you sign in, so it can only be changed by an administrator."
        />
        <TextField
          id="epocus-settings-telephone"
          variant="outlined"
          margin="dense"
          fullWidth
          label="Mobile phone number"
          placeholder="+234…"
          value={telephoneValue}
          onChange={(e) => {
            setTelephoneValue(e.target.value);
            setTelephoneError(false);
          }}
          error={telephoneError}
          helperText={telephoneError ? PHONE_ERROR_MESSAGE : undefined}
        />
        <TextField
          id="epocus-settings-npi"
          variant="outlined"
          margin="dense"
          fullWidth
          label="NPI"
          placeholder="10 digits"
          value={npiValue}
          onChange={(e) => {
            setNpiValue(e.target.value);
            setNpiError(false);
          }}
          error={npiError}
          helperText={
            npiError
              ? NPI_ERROR_MESSAGE
              : "Your National Provider Identifier. Studies you sign can't be billed without it."
          }
          inputProps={{ inputMode: "numeric" }}
        />
      </FieldsColumn>

      <Button
        variant="contained"
        color="primary"
        disabled={loading}
        style={{ alignSelf: "flex-end" }}
        onClick={handleSave}
      >
        Save
      </Button>
    </SettingsCard>
  );
};
